/* ruleEngine.js — derived, READ-ONLY layout diagnostics (v0.54).
 *
 * The reusable rule framework every future intelligent feature builds on
 * (auto-fillers, corner recommendation, worktop planning, manufacturing checks).
 * It EVALUATES a layout and returns structured findings — it never mutates the
 * model, never blocks placement (that stays validatePlacement's job), never
 * touches UI or serialization.
 *
 * Architecture:
 *   buildRuleContext(input)  — ONE shared derived context per evaluation:
 *                              WallGraph (topology), RunGraph (lines/runs),
 *                              per-wall inner spans, per-line member indexes,
 *                              corner-terminal table. Rules only READ it.
 *   RULES                    — flat registry; every rule is independent:
 *                              { id, category, defaultSeverity, title,
 *                                evaluate(ctx) → finding[] }.
 *   evaluateRules(input, o)  — run all / a filtered subset; normalized results.
 *   runRule(id, input, o)    — run ONE rule in isolation.
 *
 * Finding shape (RuleResult):
 *   { ruleId, category, severity: 'info'|'warning'|'error', appliesTo: [ids],
 *     passed: boolean, message, measuredValue?, expectedValue?, suggestedFix? }
 *   - `passed:true` findings are MEASUREMENTS (e.g. corner-void telemetry).
 *   - suggestedFix is a structured, side-effect-free hint
 *     ({ kind: 'filler'|'close-gap'|'slide'|... , ...params }) for future
 *     auto-solve features — nothing here applies it.
 *
 * Thresholds come from the engine's own rulebook (LHC_RULES.FILLER_MIN/MAX,
 * RULE_EPS, RUN_GAP_TOL) plus rule-specific defaults in DEFAULT_RULE_PARAMS —
 * all overridable per evaluation via input.params (testable, dealer-tunable).
 */
import { LHC_RULES, RULE_EPS, WALL_THICKNESS, DEFAULT_WALL_HEIGHT } from "./constants";
import { getWallGraph } from "./wallGraph";
import { buildRunGraph, linesOfRole, RUN_GAP_TOL } from "./runs";
import { wallInnerSpan } from "./snapping";
import { effDef } from "./pricing";
import { golaChannels, golaGapBetween, GOLA_SIDE_CLEARANCE_M } from "./golaClearance";
import { pairPanelGap } from "./sidePanelClearance";
import { lhcVSpan } from "./placement";
import { buildEndPanels } from "./endPanels";
import { roleBaseY, getWorktopTopM } from "./rules";

export const DEFAULT_RULE_PARAMS = {
  cornerClearMin: 0.05,  // m — front/handle clearance from a return wall's inner face
  dwCornerMin: 0.05,     // m — dishwasher door clearance from an inner corner
  ovenSideMin: 0.05,     // m — oven housing side clearance at a wall end
  fridgeVentMin: 0.03,   // m — fridge tower top ventilation headroom
  hobSinkMin: 0.30,      // m — min edge separation between a hob and a sink (splash + safety zone)
  blindReveal: 0.0,      // m — extra pull-out reveal a blind corner needs beyond depth
  tiltMargin: LHC_RULES.TILT_MARGIN, // m — ceiling safety margin a tall unit's tilt-up diagonal must clear
  worktopDepthTolM: 0.01, // m — max depth difference between flush worktop-carrying base neighbours before the worktop front-line jogs
  endStripMax: 0.60,     // m — an end gap wider than this is OPEN SPACE (unfinished design), not a stranded strip
  landingMin: 0.30,      // m — minimum worktop landing space beside a hob / sink (NKBA)
  triLegMin: 1.2,        // m — work-triangle leg guideline (NKBA 1.2–2.7 per leg)
  triLegMax: 2.7,
  triSumMax: 7.9,        // m — work-triangle total guideline (NKBA ≤ 7.9)
  dwSinkMax: 0.9,        // m — dishwasher-to-sink ideal plumbing distance
  /* ADVANCED CHECKS (2026-08-20 user: "in design check add advanced things
   * to check") — all technician-tunable via blob.ruleParams like the rest. */
  hoodAboveMin: 0.60,    // m — extractor bottom above the worktop: minimum (fire/headroom)
  hoodAboveMax: 0.90,    // m — …and maximum (capture zone stops working)
  aisleMin: 1.20,        // m — clear aisle between facing runs (NKBA work aisle)
  fridgeHobGapMin: 0.30, // m — fridge flank to hob edge (heat transfer + energy)
  hobWindowClear: 0.0,   // m — a hob may not sit under an opening window (0 = no overlap at all)
};

const mm = (v) => Math.round(v * 1000) + " mm";
const M1 = (v) => (Math.round(v * 100) / 100).toFixed(2) + " m";
/* an appliance FUNCTION lives either in the module itself (def.subtype), in a
 * fitted equipment pick (cfg.eqXxx — the technician's equipment library), or
 * in the SKU code — all three are real data the design carries. */
const cfgHas = (p, k) => { const v = (p.config || {})[k]; return v != null && v !== -1 && v !== ""; };
const isSinkM = (m) => m.def.subtype === "sink" || cfgHas(m.p, "eqSinks") || /(^|-)sk-/i.test(m.def.code || "");
const isHobM = (m) => m.def.subtype === "hob" || cfgHas(m.p, "eqHobs") || /(^|-)hb-/i.test(m.def.code || "");
const isFridgeM = (m) => m.def.subtype === "fridge" || cfgHas(m.p, "eqFridges") || (m.def.role === "cabinet-tall" && /fr/i.test(m.def.code || ""));
const isHoodM = (m) => m.def.subtype === "hood" || cfgHas(m.p, "eqHoods") || /hood|(^|-)hd-/i.test(m.def.code || "");
const isDWM = (m) => m.def.subtype === "dishwasher" || cfgHas(m.p, "eqDishwashers") || /dw|dish/i.test(m.def.code || "");
/* world position of a member's centre — walls carry start + unit dir. */
const worldMid = (ctx, m) => {
  const w = ctx.walls.find((x) => x.id === m.wallId);
  const wi = ctx.graph.walls.get(m.wallId);
  if (!w || !wi || !Array.isArray(w.start)) return null;
  const t = (m.left + m.right) / 2;
  return [w.start[0] + wi.dir[0] * t, w.start[1] + wi.dir[1] * t];
};
const distW = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
/* continuous FLUSH worktop run space beside a member (landing space). */
const sideSpace = (ctx, m, side) => {
  const lineIdx = (ctx.byWallLine.get(m.wallId) || { base: [] }).base;
  const ms = lineIdx.map((i) => ctx.members[i]).sort((a, b) => a.left - b.left);
  const at = ms.findIndex((x) => x.p.id === m.p.id);
  if (at < 0) return 0;
  let space = 0;
  const step = side === "left" ? -1 : 1;
  for (let k = at + step; k >= 0 && k < ms.length; k += step) {
    const prev = ms[k - step], cur = ms[k];
    const gap = side === "left" ? prev.left - cur.right : cur.left - prev.right;
    if (gap > LHC_RULES.FILLER_MIN) break;              // run breaks — landing ends
    if (isHobM(cur) || isSinkM(cur)) break;             // another wet/hot zone is not landing space
    if (!carriesWorktop(cur.def)) break;
    space += cur.def.width || 0;
  }
  return space;
};
const isCornerDef = (def) => (def.subcategory || "") === "Corner";
const isBlindDef = (def) => isCornerDef(def) && /bl/i.test(def.code || "");
const carriesWorktop = (def) =>
  def.role === "cabinet-base" || (def.role === "appliance" && (def.subtype === "hob" || def.subtype === "sink"));

/** ONE derived context per evaluation — rules only read from it. */
export function buildRuleContext(input) {
  const walls = input.walls || [];
  const placements = input.placements || [];
  const lib = input.lib || {};
  const params = { ...DEFAULT_RULE_PARAMS, ...(input.params || {}) };
  const graph = getWallGraph(walls);
  const runGraph = buildRunGraph(placements, lib, walls);

  // Eligible members (mirror runs.js) + full placement census for orphan checks.
  const members = [];
  placements.forEach((p) => {
    if (!p.wallId || p.island) return;
    const def = effDef(p, lib);
    if (!def || !graph.walls.get(p.wallId)) return;
    const lines = linesOfRole(def.role);
    const left = p.absLeft || 0;
    members.push({ p, def, lines, left, right: left + (def.width || 0), wallId: p.wallId });
  });

  const spans = new Map(); // wallId → wallInnerSpan
  walls.forEach((w) => { if (graph.walls.get(w.id)) spans.set(w.id, wallInnerSpan(w, walls)); });

  // wallId → line → member indexes sorted by left edge
  const byWallLine = new Map();
  members.forEach((m, i) => {
    if (!byWallLine.has(m.wallId)) byWallLine.set(m.wallId, { base: [], upper: [] });
    m.lines.forEach((ln) => byWallLine.get(m.wallId)[ln].push(i));
  });
  byWallLine.forEach((lines) => {
    lines.base.sort((a, b) => members[a].left - members[b].left);
    lines.upper.sort((a, b) => members[a].left - members[b].left);
  });

  // Corner-terminal table: node → [{ wallId, end, mi, d }] (nearest BASE-line member
  // per incident wall end, distance measured to the joint — same as runs.js).
  const cornerTerminals = new Map();
  graph.nodes.forEach((node) => {
    if (node.kind === "free" || node.kind === "continuation") return;
    const list = [];
    node.ends.forEach((e) => {
      const wi = graph.walls.get(e.wallId);
      const lineIdx = byWallLine.get(e.wallId);
      if (!wi || !lineIdx) return;
      let best = null;
      lineIdx.base.forEach((mi) => {
        const m = members[mi];
        const d = e.end === "start" ? m.left : wi.len - m.right;
        if (d >= 0 && (!best || d < best.d)) best = { wallId: e.wallId, end: e.end, mi, d };
      });
      if (best) list.push(best);
    });
    if (list.length) cornerTerminals.set(node.id, list);
  });

  // Manufacturing (Group B): finished end-panel detection, derived once and reused by
  // the mfg.end-panel-needed rule. Additive — no existing ctx field changes.
  const endPanels = buildEndPanels(walls, placements, lib);

  // WALL OPENINGS (2026-08-20 advanced checks): windows/doors ride the context
  // so safety rules can see them. Shape = the app's wallOpenings records
  // ({ wallId, type, u|offset, width, sill|y, height }). Additive field.
  const openings = input.openings || [];

  return { walls, placements, lib, params, graph, runGraph, members, spans, byWallLine, cornerTerminals, endPanels, openings, wizard: input.wizard || null };
}

/* ── helpers shared by rules (read-only) ─────────────────────────────────── */
const consecutivePairs = (ctx, fn) => {
  ctx.byWallLine.forEach((lines, wallId) => {
    ["base", "upper"].forEach((line) => {
      const idxs = lines[line];
      for (let k = 0; k < idxs.length - 1; k++) {
        const a = ctx.members[idxs[k]], b = ctx.members[idxs[k + 1]];
        // a member of both lines pairs once per line — dedupe on the base pass
        if (line === "upper" && a.lines.includes("base") && b.lines.includes("base")) continue;
        /* GOLA-AWARE GAP (2026-08-30): the 30mm a gola channel demands between
         * two members is construction, not a gap — subtract it, so 30mm beside
         * a gola reads as flush (no dead-band / filler / worktop break). A
         * member standing INSIDE the channel is the channel rule's finding
         * alone (one owner per defect — the solver's ambiguity guard would
         * otherwise drop both fixes). */
        /* SIDE PANELS (2026-09-07): a decor board standing between two members
         * is construction too — the space it fills is not a gap to report. */
        const gg = golaGapBetween(a.p, b.p, ctx.placements, (x) => effDef(x, ctx.lib))
          + pairPanelGap(a.p, effDef(a.p, ctx.lib), b.p, effDef(b.p, ctx.lib));
        const rawGap = b.left - a.right;
        if (gg > 0 && rawGap < gg - RULE_EPS && rawGap > -RULE_EPS) continue;
        fn(a, b, rawGap - gg, wallId, line);
      }
    });
  });
};
const insideCornerPairs = (ctx, fn) => {
  ctx.graph.nodes.forEach((node) => {
    if (node.kind !== "corner" || node.cornerKind !== "inside") return;
    const terms = ctx.cornerTerminals.get(node.id);
    if (!terms || terms.length < 2) return;
    for (let i = 0; i < terms.length; i++)
      for (let j = i + 1; j < terms.length; j++)
        if (terms[i].wallId !== terms[j].wallId) fn(node, terms[i], terms[j]);
  });
};

/* ── THE RULES (7 categories, 25 rules) ──────────────────────────────────── */
export const RULES = [

  /* ═══ RUN INTEGRITY ═══ */
  {
    id: "run.duplicate-occupancy", category: "run", defaultSeverity: "error",
    title: "Duplicate occupancy",
    evaluate(ctx) {
      const out = [];
      ctx.byWallLine.forEach((lines) => ["base", "upper"].forEach((line) => {
        const idxs = lines[line];
        for (let k = 0; k < idxs.length - 1; k++) {
          const a = ctx.members[idxs[k]], b = ctx.members[idxs[k + 1]];
          if (line === "upper" && a.lines.includes("base") && b.lines.includes("base")) continue;
          if (Math.abs(a.left - b.left) <= 0.001) out.push({
            appliesTo: [a.p.id, b.p.id],
            message: `Two units occupy the same position (${a.def.code || a.p.type} / ${b.def.code || b.p.type}) — duplicated placement?`,
            measuredValue: Math.abs(a.left - b.left), expectedValue: RUN_GAP_TOL,
          });
        }
      }));
      return out;
    },
  },
  {
    id: "gola.channel-clearance", category: "clearance", defaultSeverity: "error",
    title: "Gola channel clearance",
    /* 2026-08-30 user: "even wall units should hold the 30mm as soon as we
     * make the gola height connect — everything the gola touches adds exactly
     * 30mm between cabinets". The extended post is a 30mm channel over its
     * full height; any module whose band it passes and which stands inside
     * it is a violation with a structured slide (minimal displacement). */
    evaluate(ctx) {
      const out = [];
      const byWall = new Map();
      ctx.members.forEach((m) => {
        if (!m.wallId) return;
        if (!byWall.has(m.wallId)) byWall.set(m.wallId, []);
        byWall.get(m.wallId).push(m);
      });
      const effOf = (x) => effDef(x, ctx.lib);
      byWall.forEach((ms, wallId) => {
        const chs = golaChannels(wallId, ctx.placements, effOf);
        if (!chs.length) return;
        ms.forEach((m) => {
          const band = lhcVSpan(m.p, m.def);
          chs.forEach((ch) => {
            if (ch.carrierId === m.p.id) return;
            if (!(ch.bot < band[1] - 1e-3 && ch.top > band[0] + 1e-3)) return;
            const intr = Math.min(m.right, ch.x1) - Math.max(m.left, ch.x0);
            if (intr <= RULE_EPS) return;
            const goRight = ch.x1 - m.left, goLeft = m.right - ch.x0;
            const deltaM = goRight <= goLeft ? goRight : -goLeft;
            const carrier = (ctx.placements || []).find((q) => q && q.id === ch.carrierId);
            const cdef = carrier ? effOf(carrier) : null;
            out.push({
              appliesTo: [m.p.id, ch.carrierId],
              message: `${m.def.code || m.p.type} stands ${mm(intr)} inside the gola channel of ${(cdef && cdef.code) || (carrier && carrier.type) || "a gola module"} — the gola needs 30 mm clear`,
              measuredValue: GOLA_SIDE_CLEARANCE_M - intr, expectedValue: GOLA_SIDE_CLEARANCE_M,
              suggestedFix: { kind: "slide", placementId: m.p.id, deltaM },
            });
          });
        });
      });
      return out;
    },
  },
  {
    id: "run.negative-spacing", category: "run", defaultSeverity: "error",
    title: "Negative spacing (adjacent overlap)",
    evaluate(ctx) {
      const out = [];
      consecutivePairs(ctx, (a, b, gap) => {
        if (Math.abs(a.left - b.left) <= 0.001) return; // duplicate-occupancy's case
        if (gap < -RULE_EPS) out.push({
          appliesTo: [a.p.id, b.p.id],
          message: `${b.def.code || b.p.type} eats ${mm(-gap)} into ${a.def.code || a.p.type}`,
          measuredValue: gap, expectedValue: 0,
          suggestedFix: { kind: "slide", placementId: b.p.id, deltaM: -gap },
        });
      });
      return out;
    },
  },
  {
    id: "run.overlap", category: "run", defaultSeverity: "error",
    title: "Non-adjacent overlap (containment/spanning)",
    evaluate(ctx) {
      const out = [];
      ctx.byWallLine.forEach((lines) => ["base", "upper"].forEach((line) => {
        const idxs = lines[line];
        for (let x = 0; x < idxs.length; x++) for (let y = x + 2; y < idxs.length; y++) {
          const a = ctx.members[idxs[x]], b = ctx.members[idxs[y]]; // skip consecutive (y=x+1)
          if (line === "upper" && a.lines.includes("base") && b.lines.includes("base")) continue;
          const ov = Math.min(a.right, b.right) - Math.max(a.left, b.left);
          if (ov > RULE_EPS) out.push({
            appliesTo: [a.p.id, b.p.id],
            message: `${a.def.code || a.p.type} and ${b.def.code || b.p.type} overlap by ${mm(ov)} (one spans the other)`,
            measuredValue: ov, expectedValue: 0,
          });
        }
      }));
      return out;
    },
  },
  {
    id: "run.orphan", category: "run", defaultSeverity: "error",
    title: "Orphan cabinet",
    evaluate(ctx) {
      const out = [];
      ctx.placements.forEach((p) => {
        if (p.island) return;
        const def = effDef(p, ctx.lib);
        if (p.wallId && !ctx.graph.walls.get(p.wallId)) out.push({
          appliesTo: [p.id], severity: "error",
          message: `Cabinet references missing/degenerate wall '${p.wallId}'`,
        });
        else if (!def) out.push({
          appliesTo: [p.id], severity: "warning",
          message: `Unknown SKU '${p.type}' — not in the module library`,
        });
        else if (!p.wallId) out.push({
          appliesTo: [p.id], severity: "info",
          message: "Wall-less legacy placement (back-wall row fallback)",
        });
      });
      return out;
    },
  },
  {
    id: "run.order-audit", category: "run", defaultSeverity: "error",
    title: "Run ordering audit (derived-invariant canary)",
    evaluate(ctx) {
      const out = [];
      ctx.runGraph.runs.forEach((run) => {
        const perWall = new Map();
        run.members.forEach((pid) => {
          const m = ctx.members.find((x) => x.p.id === pid);
          if (!m) return;
          if (!perWall.has(m.wallId)) perWall.set(m.wallId, []);
          perWall.get(m.wallId).push(m);
        });
        perWall.forEach((ms) => {
          for (let k = 0; k < ms.length - 1; k++)
            if (ms[k + 1].left < ms[k].left - 1e-9) out.push({
              appliesTo: [ms[k].p.id, ms[k + 1].p.id],
              message: `Run '${run.id}' member order contradicts positions — corrupted state?`,
            });
        });
      });
      return out;
    },
  },

  /* ═══ CORNER RULES ═══ */
  {
    id: "corner.insufficient-clearance", category: "corner", defaultSeverity: "warning",
    title: "Insufficient corner clearance",
    evaluate(ctx) {
      const out = [];
      insideCornerPairs(ctx, (node, tA, tB) => {
        [[tA, tB], [tB, tA]].forEach(([t, other]) => {
          const m = ctx.members[t.mi];
          if (isCornerDef(m.def)) return;                 // a corner unit IS the solution
          const clearance = t.d - WALL_THICKNESS / 2;     // from the return wall's inner face
          if (clearance >= 0 && clearance < ctx.params.cornerClearMin && other) out.push({
            appliesTo: [m.p.id], measuredValue: clearance, expectedValue: ctx.params.cornerClearMin,
            message: `${m.def.code || m.p.type} front is ${mm(clearance)} from the return wall at the corner — doors/handles will foul (min ${mm(ctx.params.cornerClearMin)})`,
            suggestedFix: { kind: "corner-filler", nodeId: node.id, widthM: Math.max(LHC_RULES.FILLER_MIN, ctx.params.cornerClearMin - clearance) },
          });
        });
      });
      return out;
    },
  },
  {
    id: "corner.blind-conflict", category: "corner", defaultSeverity: "warning",
    title: "Blind corner conflict",
    evaluate(ctx) {
      const out = [];
      insideCornerPairs(ctx, (node, tA, tB) => {
        [[tA, tB], [tB, tA]].forEach(([t, other]) => {
          const m = ctx.members[t.mi];
          if (!isBlindDef(m.def)) return;
          const otherM = ctx.members[other.mi];
          const avail = other.d + WALL_THICKNESS / 2;     // return run stand-off from the joint
          const need = m.def.depth + ctx.params.blindReveal;
          if (avail < need) out.push({
            appliesTo: [m.p.id, otherM.p.id], measuredValue: avail, expectedValue: need,
            message: `Blind corner ${m.def.code} needs ${mm(need)} on the return wall; the adjacent run stands ${mm(avail)} off — pull-out blocked`,
            suggestedFix: { kind: "slide", placementId: otherM.p.id, deltaM: need - avail },
          });
        });
      });
      return out;
    },
  },
  {
    id: "corner.impossible-depth", category: "corner", defaultSeverity: "error",
    title: "Impossible corner cabinet depth",
    evaluate(ctx) {
      const out = [];
      ctx.graph.nodes.forEach((node) => {
        if (node.kind !== "corner" || node.cornerKind !== "inside") return;
        const terms = ctx.cornerTerminals.get(node.id) || [];
        terms.forEach((t) => {
          const m = ctx.members[t.mi];
          // Only corner units SEATED at the joint — in this engine "flush at the
          // corner" means the inner-span inset (WT/2), not distance zero.
          if (!isCornerDef(m.def) || t.d > WALL_THICKNESS / 2 + RULE_EPS) return;
          node.ends.forEach((e) => {
            if (e.wallId === t.wallId) return;
            const otherWi = ctx.graph.walls.get(e.wallId);
            const otherSpan = ctx.spans.get(e.wallId);
            if (!otherWi || !otherSpan) return;
            const avail = otherSpan.innerLen + WALL_THICKNESS / 2; // depth room along the return wall
            if (avail < m.def.depth) out.push({
              appliesTo: [m.p.id, e.wallId], measuredValue: avail, expectedValue: m.def.depth,
              message: `Corner unit ${m.def.code} is ${mm(m.def.depth)} deep but the return wall '${e.wallId}' only offers ${mm(avail)} — physically impossible`,
            });
          });
        });
      });
      return out;
    },
  },
  {
    id: "corner.adjacent-interference", category: "corner", defaultSeverity: "warning",
    title: "Adjacent run interference at corner",
    evaluate(ctx) {
      const out = [];
      insideCornerPairs(ctx, (node, tA, tB) => {
        const mA = ctx.members[tA.mi], mB = ctx.members[tB.mi];
        if (isCornerDef(mA.def) || isCornerDef(mB.def)) return; // solved corner
        const inA = tA.d - WALL_THICKNESS / 2, inB = tB.d - WALL_THICKNESS / 2;
        if (inA < mB.def.depth && inB < mA.def.depth) out.push({
          appliesTo: [mA.p.id, mB.p.id],
          measuredValue: Math.min(inA, inB), expectedValue: Math.max(mA.def.depth, mB.def.depth),
          message: `Runs on '${tA.wallId}' and '${tB.wallId}' both crowd the corner — fronts clash in the corner square (no corner unit present)`,
          suggestedFix: { kind: "corner-unit", nodeId: node.id },
        });
      });
      return out;
    },
  },

  /* ═══ WALL RULES ═══ */
  {
    id: "wall.cabinet-outside-bounds", category: "wall", defaultSeverity: "error",
    title: "Cabinet outside wall bounds",
    evaluate(ctx) {
      const out = [];
      ctx.members.forEach((m) => {
        const wi = ctx.graph.walls.get(m.wallId);
        const sp = ctx.spans.get(m.wallId);
        if (!wi || !sp) return;
        // HARD bound = the signed span's outer limit: at a CONVEX corner the legal span
        // extends PAST the centerline end to the visible face corner (off < 0), so the
        // error line is min(0, off) / max(len, len − off) — a unit flush on a convex
        // corner is legal, never "past the end of the wall" (2026-09-28 corner contract).
        const hardLo = Math.min(0, sp.off0), hardHi = Math.max(wi.len, wi.len - sp.off1);
        if (m.left < hardLo - RULE_EPS || m.right > hardHi + RULE_EPS) out.push({
          appliesTo: [m.p.id], severity: "error",
          measuredValue: m.right, expectedValue: wi.len,
          message: `${m.def.code || m.p.type} extends past the end of wall '${m.wallId}' (${mm(m.right)} on a ${mm(wi.len)} wall)`,
        });
        else if (m.left < sp.off0 - RULE_EPS || m.right > wi.len - sp.off1 + RULE_EPS) out.push({
          appliesTo: [m.p.id], severity: "warning",
          measuredValue: Math.min(m.left - sp.off0, wi.len - sp.off1 - m.right), expectedValue: 0,
          message: `${m.def.code || m.p.type} intrudes into the corner block of wall '${m.wallId}'`,
        });
      });
      return out;
    },
  },
  {
    id: "wall.overrun", category: "wall", defaultSeverity: "error",
    title: "Run exceeds wall capacity",
    evaluate(ctx) {
      const out = [];
      ctx.byWallLine.forEach((lines, wallId) => ["base", "upper"].forEach((line) => {
        const sp = ctx.spans.get(wallId);
        if (!sp || !lines[line].length) return;
        const total = lines[line].reduce((s, i) => s + (ctx.members[i].def.width || 0), 0);
        if (total > sp.innerLen + RULE_EPS) out.push({
          appliesTo: lines[line].map((i) => ctx.members[i].p.id),
          measuredValue: total, expectedValue: sp.innerLen,
          message: `${line} line on '${wallId}' totals ${mm(total)} but the wall's inner span is ${mm(sp.innerLen)} — overrun by ${mm(total - sp.innerLen)}`,
        });
      }));
      return out;
    },
  },
  {
    id: "wall.zero-length", category: "wall", defaultSeverity: "warning",
    title: "Zero-length wall",
    evaluate(ctx) {
      return ctx.walls
        .filter((w) => w && w.id != null && !ctx.graph.walls.get(w.id))
        .map((w) => ({ appliesTo: [w.id], message: `Wall '${w.id}' is degenerate (zero/near-zero length) — invisible but still in the model` }));
    },
  },
  {
    id: "wall.disconnected", category: "wall", defaultSeverity: "info",
    title: "Disconnected wall",
    evaluate(ctx) {
      const out = [];
      ctx.graph.walls.forEach((wi, wallId) => {
        const w = ctx.walls.find((x) => x.id === wallId);
        if (!w || (w.style || "standard") !== "standard") return; // partitions/peninsulas dangle by design
        const n0 = ctx.graph.nodes.find((n) => n.id === wi.startNode);
        const n1 = ctx.graph.nodes.find((n) => n.id === wi.endNode);
        if (n0.kind !== "free" || n1.kind !== "free") return;
        const carries = ctx.members.some((m) => m.wallId === wallId);
        out.push({
          appliesTo: [wallId], severity: carries ? "warning" : "info",
          message: `Wall '${wallId}' touches no other wall${carries ? " but carries cabinets" : ""}`,
        });
      });
      return out;
    },
  },
  {
    id: "wall.room-ownership", category: "wall", defaultSeverity: "warning",
    title: "Invalid room ownership",
    evaluate(ctx) {
      if (!ctx.graph.rooms.length) return []; // no rooms yet (open plan) — nothing to own
      const out = [];
      ctx.graph.walls.forEach((wi, wallId) => {
        const w = ctx.walls.find((x) => x.id === wallId);
        if (!w || (w.style || "standard") !== "standard") return;
        const owned = wi.roomSides.left.length + wi.roomSides.right.length > 0;
        const carries = ctx.members.filter((m) => m.wallId === wallId);
        if (!owned && carries.length) out.push({
          appliesTo: [wallId, ...carries.map((m) => m.p.id)],
          message: `Wall '${wallId}' borders no room, but ${carries.length} cabinet(s) hang on it — outside the plan?`,
        });
      });
      return out;
    },
  },

  /* ═══ GAP RULES ═══ */
  {
    id: "gap.below-min-filler", category: "gap", defaultSeverity: "warning",
    title: "Gap below minimum filler",
    evaluate(ctx) {
      const out = [];
      consecutivePairs(ctx, (a, b, gap) => {
        if (gap > RUN_GAP_TOL && gap < LHC_RULES.FILLER_MIN) out.push({
          appliesTo: [a.p.id, b.p.id], measuredValue: gap, expectedValue: LHC_RULES.FILLER_MIN,
          message: `${mm(gap)} gap between ${a.def.code || a.p.type} and ${b.def.code || b.p.type} — too wide to be flush, too narrow for a filler (min ${mm(LHC_RULES.FILLER_MIN)})`,
          suggestedFix: { kind: "close-gap", placementId: b.p.id, deltaM: -gap },
        });
      });
      return out;
    },
  },
  {
    id: "gap.excessive-end", category: "gap", defaultSeverity: "info",
    title: "Excessive end gap",
    evaluate(ctx) {
      const out = [];
      ctx.byWallLine.forEach((lines, wallId) => ["base", "upper"].forEach((line) => {
        const idxs = lines[line];
        const sp = ctx.spans.get(wallId);
        const wi = ctx.graph.walls.get(wallId);
        if (!idxs.length || !sp || !wi) return;
        const first = ctx.members[idxs[0]], last = ctx.members[idxs[idxs.length - 1]];
        [["start", first.left - sp.off0, first], ["end", wi.len - sp.off1 - last.right, last]].forEach(([endName, gap, m]) => {
          // 2026-08-18 retune: a WIDE-open end is just an unfinished design —
          // flagging it on every in-progress plan was noise. Only a STRANDED
          // strip (too wide for a filler, too narrow for any unit) is a real
          // issue the designer must resolve.
          if (gap > LHC_RULES.FILLER_MAX && gap < ctx.params.endStripMax) out.push({
            appliesTo: [m.p.id], measuredValue: gap, expectedValue: LHC_RULES.FILLER_MAX,
            message: `${mm(gap)} stranded strip at the ${endName} of the ${line} line on '${wallId}' — too wide for a filler (max ${mm(LHC_RULES.FILLER_MAX)}), too narrow for a unit: reposition the run or fit an end panel`,
            suggestedFix: { kind: "review-end-gap", wallId, end: endName, gapM: gap },
          });
        });
      }));
      return out;
    },
  },
  {
    id: "gap.corner-void", category: "gap", defaultSeverity: "info",
    title: "Corner void measurement",
    evaluate(ctx) {
      const out = [];
      // Pure telemetry (passed:true): every corner transition reports its measured
      // void so downstream features (corner posts, fillers, worktop mitres) can
      // consume the number without re-deriving geometry.
      ctx.runGraph.runs.forEach((run) => run.transitions.forEach((t) => {
        if (t.kind !== "corner") return;
        out.push({
          appliesTo: [t.aId, t.bId], passed: true,
          measuredValue: t.gapM,
          message: `Corner void at ${t.cornerKind || "corner"} joint: ${mm(t.gapM)} across '${run.id}'`,
        });
      }));
      return out;
    },
  },
  {
    id: "gap.worktop-discontinuity", category: "gap", defaultSeverity: "info",
    title: "Worktop discontinuity",
    evaluate(ctx) {
      const out = [];
      // Consecutive WORKTOP-ELIGIBLE members on one wall (outputs.js eligibility:
      // cabinet-base + hob/sink appliances) separated by a fillable-or-larger gap:
      // the slab will be cut there. (Dead-band gaps < FILLER_MIN are the
      // below-min-filler rule's finding — disjoint by design.)
      ctx.byWallLine.forEach((lines, wallId) => {
        const idxs = lines.base.filter((i) => carriesWorktop(ctx.members[i].def));
        for (let k = 0; k < idxs.length - 1; k++) {
          const a = ctx.members[idxs[k]], b = ctx.members[idxs[k + 1]];
          // a gola channel between them is construction, not a slab cut (2026-08-30)
          const gap = b.left - a.right - golaGapBetween(a.p, b.p, ctx.placements, (x) => effDef(x, ctx.lib));
          if (gap >= LHC_RULES.FILLER_MIN) out.push({
            appliesTo: [a.p.id, b.p.id], measuredValue: gap, expectedValue: RUN_GAP_TOL,
            message: `Worktop on '${wallId}' splits at a ${mm(gap)} gap between ${a.def.code || a.p.type} and ${b.def.code || b.p.type}`,
            suggestedFix: gap <= LHC_RULES.FILLER_MAX ? { kind: "filler", widthM: gap, afterPlacementId: a.p.id } : { kind: "review-gap", gapM: gap },
          });
        }
      });
      return out;
    },
  },

  /* ═══ APPLIANCE RULES ═══ */
  {
    id: "appliance.dishwasher-corner", category: "appliance", defaultSeverity: "warning",
    title: "Dishwasher too close to a corner",
    evaluate(ctx) {
      const out = [];
      ctx.members.forEach((m) => {
        if (m.def.subtype !== "dishwasher") return;
        const sp = ctx.spans.get(m.wallId); const wi = ctx.graph.walls.get(m.wallId);
        if (!sp || !wi) return;
        // only ends that are real inner corners (span inset > 0 means a joint there)
        [["start", m.left - sp.off0, sp.off0 > 0], ["end", wi.len - sp.off1 - m.right, sp.off1 > 0]].forEach(([endName, clear, isCorner]) => {
          if (isCorner && clear < ctx.params.dwCornerMin) out.push({
            appliesTo: [m.p.id], measuredValue: clear, expectedValue: ctx.params.dwCornerMin,
            message: `Dishwasher ${mm(clear)} from the ${endName}-corner of '${m.wallId}' — open door blocks the return run (min ${mm(ctx.params.dwCornerMin)})`,
            suggestedFix: { kind: "slide", placementId: m.p.id, deltaM: (endName === "start" ? 1 : -1) * (ctx.params.dwCornerMin - clear) },
          });
        });
      });
      return out;
    },
  },
  {
    id: "appliance.oven-side-clearance", category: "appliance", defaultSeverity: "warning",
    title: "Oven housing side clearance",
    evaluate(ctx) {
      const out = [];
      ctx.members.forEach((m) => {
        if (m.def.subtype !== "oven") return;
        const sp = ctx.spans.get(m.wallId); const wi = ctx.graph.walls.get(m.wallId);
        if (!sp || !wi) return;
        [["start", m.left - sp.off0, sp.off0 > 0], ["end", wi.len - sp.off1 - m.right, sp.off1 > 0]].forEach(([endName, clear, isCorner]) => {
          if (isCorner && clear < ctx.params.ovenSideMin) out.push({
            appliesTo: [m.p.id], measuredValue: clear, expectedValue: ctx.params.ovenSideMin,
            message: `Oven housing ${mm(clear)} from the ${endName}-corner of '${m.wallId}' — door + rack access needs ${mm(ctx.params.ovenSideMin)}`,
          });
        });
      });
      return out;
    },
  },
  {
    id: "appliance.fridge-ventilation", category: "appliance", defaultSeverity: "warning",
    title: "Refrigerator ventilation headroom",
    evaluate(ctx) {
      const out = [];
      ctx.members.forEach((m) => {
        if (m.def.subtype !== "fridge") return;
        const w = ctx.walls.find((x) => x.id === m.wallId);
        const wallH = (w && w.height) || DEFAULT_WALL_HEIGHT;
        const headroom = wallH - (m.def.height || 0);
        if (headroom < ctx.params.fridgeVentMin) out.push({
          appliesTo: [m.p.id], measuredValue: headroom, expectedValue: ctx.params.fridgeVentMin,
          message: `Fridge tower leaves ${mm(headroom)} to the ceiling — ventilation needs ${mm(ctx.params.fridgeVentMin)}`,
        });
      });
      return out;
    },
  },
  {
    id: "appliance.hob-sink-separation", category: "appliance", defaultSeverity: "warning",
    title: "Hob and sink too close",
    evaluate(ctx) {
      const out = [];
      // Same-wall hob↔sink pairs need a work/safety zone between their edges —
      // splashing water onto a burner is the classic layout fault every rule
      // book flags. Cross-wall pairs are fine (the corner separates them).
      ctx.byWallLine.forEach((lines, wallId) => {
        const idxs = lines.base || [];
        const hs = idxs.map((i) => ctx.members[i]).filter((m) => m.def.subtype === "hob" || m.def.subtype === "sink");
        for (let a = 0; a < hs.length; a++) {
          for (let b = a + 1; b < hs.length; b++) {
            const A = hs[a], B = hs[b];
            if (A.def.subtype === B.def.subtype) continue;
            const gap = Math.max(A.left, B.left) - Math.min(A.right, B.right);
            if (gap < ctx.params.hobSinkMin) out.push({
              appliesTo: [A.p.id, B.p.id], measuredValue: Math.max(0, gap), expectedValue: ctx.params.hobSinkMin,
              message: `Hob and sink ${gap <= 0 ? "touch" : mm(gap) + " apart"} on '${wallId}' — a ${mm(ctx.params.hobSinkMin)} work zone between water and flame is required`,
            });
          }
        }
      });
      return out;
    },
  },
  {
    id: "appliance.overlap", category: "appliance", defaultSeverity: "error",
    title: "Appliance overlap (collision-band blind spot)",
    evaluate(ctx) {
      const out = [];
      // role 'appliance' has collision band 0 — validatePlacement never sees these
      // overlaps. Hob/sink INSERTS legitimately overlay base units; slot appliances
      // (dishwasher) must not overlap anything on the base line.
      ctx.members.forEach((m) => {
        if (m.def.role !== "appliance" || m.def.subtype !== "dishwasher") return;
        const lineIdx = (ctx.byWallLine.get(m.wallId) || { base: [] }).base;
        lineIdx.forEach((oi) => {
          const o = ctx.members[oi];
          if (o.p.id === m.p.id) return;
          const ov = Math.min(m.right, o.right) - Math.max(m.left, o.left);
          if (ov > RULE_EPS) out.push({
            appliesTo: [m.p.id, o.p.id], measuredValue: ov, expectedValue: 0,
            message: `Dishwasher overlaps ${o.def.code || o.p.type} by ${mm(ov)} — slot appliances need their own opening`,
          });
        });
      });
      return out;
    },
  },

  /* ═══ CLEARANCE RULES ═══ */
  {
    id: "clearance.tall-tilt-up", category: "clearance", defaultSeverity: "warning",
    title: "Tall unit can't be tilted upright",
    evaluate(ctx) {
      const out = [];
      // Rule-form of placementPipeline.checkTiltUp: a cabinet-tall member whose
      // diagonal sqrt(height² + depth²) exceeds the ceiling minus a safety margin
      // can't be stood up from lying-down during install.
      ctx.members.forEach((m) => {
        if (m.def.role !== "cabinet-tall") return;
        const w = ctx.walls.find((x) => x.id === m.wallId);
        const wallH = (w && w.height) || DEFAULT_WALL_HEIGHT;
        const diag = Math.hypot(m.def.height || 0, m.def.depth || 0);
        const maxDiag = wallH - ctx.params.tiltMargin;
        if (diag > maxDiag) out.push({
          appliesTo: [m.p.id], measuredValue: diag, expectedValue: maxDiag,
          message: `${m.def.code || m.p.type} can't be tilted upright under this ${wallH.toFixed(2)}m ceiling — its ${mm(diag)} tilt diagonal exceeds ${mm(maxDiag)} (reduce height or depth)`,
        });
      });
      return out;
    },
  },

  /* ═══ MANUFACTURING RULES ═══ */
  {
    id: "mfg.end-panel-needed", category: "mfg", defaultSeverity: "info",
    title: "Finished end panel needed",
    evaluate(ctx) {
      // Consume the derived end-panel layer (endPanels.js buildEndPanels, memoised on
      // ctx): each genuinely EXPOSED run end that is neither tucked into a wall-return
      // corner (scribe) nor turning a corner (mitre/return) needs a decor end panel.
      // One info finding per exposed end, deterministic order (buildEndPanels sorts).
      return (ctx.endPanels || []).map((ep) => ({
        appliesTo: [ep.atPlacementId],
        message: `Exposed ${ep.role} run end at the ${ep.end} of ${ep.atPlacementId} on '${ep.wallId}' needs a finished end panel (${mm(ep.heightMm / 1000)} × ${mm(ep.depthMm / 1000)})`,
        suggestedFix: {
          kind: "end-panel",
          wallId: ep.wallId,
          end: ep.end,
          atPlacementId: ep.atPlacementId,
          heightMm: ep.heightMm,
          depthMm: ep.depthMm,
        },
      }));
    },
  },
  {
    id: "mfg.worktop-depth-consistency", category: "mfg", defaultSeverity: "warning",
    title: "Worktop depth inconsistency (front-line jog)",
    evaluate(ctx) {
      const out = [];
      // Adjacent worktop-carrying base members (cabinet-base + hob/sink inserts) that are
      // FLUSH neighbours on one wall/line — so they share one continuous worktop slab —
      // but differ in cabinet depth: the worktop front line jogs, forcing an awkward
      // stepped slab. A gap ≥ FILLER_MIN is a slab SPLIT (gap.worktop-discontinuity's
      // finding), so this stays disjoint by only pairing flush neighbours.
      ctx.byWallLine.forEach((lines, wallId) => {
        const idxs = lines.base.filter((i) => carriesWorktop(ctx.members[i].def));
        for (let k = 0; k < idxs.length - 1; k++) {
          const a = ctx.members[idxs[k]], b = ctx.members[idxs[k + 1]];
          const gap = b.left - a.right;
          if (gap >= LHC_RULES.FILLER_MIN) continue; // separate slabs — not one front line
          const dDiff = Math.abs((a.def.depth || 0) - (b.def.depth || 0));
          if (dDiff > ctx.params.worktopDepthTolM) out.push({
            appliesTo: [a.p.id, b.p.id], measuredValue: dDiff, expectedValue: ctx.params.worktopDepthTolM,
            message: `Worktop front line on '${wallId}' jogs ${mm(dDiff)} between ${a.def.code || a.p.type} (${mm(a.def.depth || 0)} deep) and ${b.def.code || b.p.type} (${mm(b.def.depth || 0)} deep) — stepped slab (tol ${mm(ctx.params.worktopDepthTolM)})`,
          });
        }
      });
      return out;
    },
  },

  /* ═══ ERGONOMICS + SAFETY (2026-08-18 — the professional rulebook) ═══ */
  {
    id: "ergonomic.work-triangle", category: "ergonomic", defaultSeverity: "warning",
    title: "Work triangle (sink – hob – fridge)",
    evaluate(ctx) {
      const sink = ctx.members.find((m) => isSinkM(m));
      const hob = ctx.members.find((m) => isHobM(m));
      const fridge = ctx.members.find((m) => isFridgeM(m));
      if (!sink || !hob || !fridge) return [];
      const ps = worldMid(ctx, sink), ph = worldMid(ctx, hob), pf = worldMid(ctx, fridge);
      if (!ps || !ph || !pf) return [];
      const legs = [
        ["sink–hob", distW(ps, ph), [sink.p.id, hob.p.id]],
        ["hob–fridge", distW(ph, pf), [hob.p.id, fridge.p.id]],
        ["fridge–sink", distW(pf, ps), [fridge.p.id, sink.p.id]],
      ];
      const total = legs.reduce((t, l) => t + l[1], 0);
      const out = [];
      legs.forEach(([name, d, ids]) => {
        if (d < ctx.params.triLegMin) out.push({
          appliesTo: ids, measuredValue: d, expectedValue: ctx.params.triLegMin,
          message: `Work triangle: ${name} is ${M1(d)} — under the ${M1(ctx.params.triLegMin)} guideline (cramped work zone)`,
        });
        else if (d > ctx.params.triLegMax) out.push({
          appliesTo: ids, severity: "info", measuredValue: d, expectedValue: ctx.params.triLegMax,
          message: `Work triangle: ${name} is ${M1(d)} — over the ${M1(ctx.params.triLegMax)} guideline (long walking path)`,
        });
      });
      if (total > ctx.params.triSumMax) out.push({
        appliesTo: [sink.p.id, hob.p.id, fridge.p.id], severity: "info",
        measuredValue: total, expectedValue: ctx.params.triSumMax,
        message: `Work triangle totals ${M1(total)} (sink–hob–fridge) — the ergonomic guideline is ≤ ${M1(ctx.params.triSumMax)}`,
      });
      if (!out.length) out.push({
        appliesTo: [sink.p.id, hob.p.id, fridge.p.id], passed: true, severity: "info",
        measuredValue: total,
        message: `Work triangle OK: ${legs.map(([n, d]) => n + " " + M1(d)).join(", ")} · total ${M1(total)}`,
      });
      return out;
    },
  },
  {
    id: "ergonomic.landing-space", category: "ergonomic", defaultSeverity: "warning",
    title: "Landing space beside hob / sink",
    evaluate(ctx) {
      const out = [];
      ctx.members.forEach((m) => {
        const hob = isHobM(m), sink = isSinkM(m);
        if (!hob && !sink) return;
        const L = sideSpace(ctx, m, "left"), R = sideSpace(ctx, m, "right");
        const best = Math.max(L, R);
        if (best < ctx.params.landingMin) out.push({
          appliesTo: [m.p.id], measuredValue: best, expectedValue: ctx.params.landingMin,
          message: `${hob ? "Hob" : "Sink"} ${m.def.code || m.p.type} has ${mm(best)} of worktop beside it — ${mm(ctx.params.landingMin)} landing space is required to set things down safely`,
        });
      });
      return out;
    },
  },
  {
    id: "safety.hob-wall-unit", category: "safety", defaultSeverity: "warning",
    title: "Wall unit directly above a hob",
    evaluate(ctx) {
      const out = [];
      ctx.members.forEach((m) => {
        if (!isHobM(m)) return;
        const lineIdx = (ctx.byWallLine.get(m.wallId) || { upper: [] }).upper;
        let hooded = false;
        lineIdx.forEach((oi) => {
          const o = ctx.members[oi];
          const ov = Math.min(m.right, o.right) - Math.max(m.left, o.left);
          if (ov <= RULE_EPS) return;
          if (isHoodM(o)) { hooded = true; return; }
          out.push({
            appliesTo: [m.p.id, o.p.id], measuredValue: ov, expectedValue: 0,
            message: `${o.def.code || o.p.type} hangs ${mm(ov)} over the hob ${m.def.code || m.p.type} — combustible front above a heat source: fit an extractor hood there instead`,
            // QUICK FIX (2026-08-20): fit the technician's extractor into
            // exactly this wall unit — the panel offers it as a button.
            suggestedFix: { kind: "hood", placementId: o.p.id },
          });
        });
        if (!hooded && !out.some((f) => f.appliesTo.includes(m.p.id))) out.push({
          appliesTo: [m.p.id], severity: "info",
          message: `No extractor hood above the hob ${m.def.code || m.p.type} — plan ventilation`,
        });
      });
      return out;
    },
  },
  {
    id: "ergonomic.dw-near-sink", category: "ergonomic", defaultSeverity: "info",
    title: "Dishwasher near the sink",
    evaluate(ctx) {
      const dw = ctx.members.find((m) => isDWM(m));
      const sink = ctx.members.find((m) => isSinkM(m));
      if (!dw || !sink) return [];
      const pd = worldMid(ctx, dw), ps = worldMid(ctx, sink);
      if (!pd || !ps) return [];
      const d = distW(pd, ps);
      if (d <= ctx.params.dwSinkMax) return [];
      return [{
        appliesTo: [dw.p.id, sink.p.id], measuredValue: d, expectedValue: ctx.params.dwSinkMax,
        message: `Dishwasher is ${M1(d)} from the sink — within ${M1(ctx.params.dwSinkMax)} keeps plumbing short and loading dry`,
      }];
    },
  },

  /* ═══ ADVANCED CHECKS (2026-08-20 user: "add advanced things to check") ═══ */
  {
    id: "safety.hob-under-window", category: "safety", defaultSeverity: "warning",
    title: "Hob under a window",
    evaluate(ctx) {
      const out = [];
      if (!ctx.openings.length) return out;
      ctx.members.forEach((m) => {
        if (!isHobM(m)) return;
        ctx.openings.forEach((o) => {
          if (!o || o.wallId !== m.wallId || String(o.type) === "door") return;
          const u0 = Number(o.u != null ? o.u : o.offset) || 0;
          const u1 = u0 + (Number(o.width) || 0.9);
          const ov = Math.min(m.right, u1) - Math.max(m.left, u0) + ctx.params.hobWindowClear;
          if (ov <= RULE_EPS) return;
          out.push({
            appliesTo: [m.p.id], measuredValue: Math.max(0, ov), expectedValue: 0,
            message: `Hob ${m.def.code || m.p.type} sits under a window — flames near curtains and reaching over burners to the handle; most codes prohibit it. Move the hob or fix the window shut`,
          });
        });
      });
      return out;
    },
  },
  {
    id: "safety.hood-height", category: "safety", defaultSeverity: "warning",
    title: "Extractor height above the hob",
    evaluate(ctx) {
      const out = [];
      const wtTop = getWorktopTopM(ctx.wizard);
      ctx.members.forEach((m) => {
        if (!isHobM(m)) return;
        const uppers = (ctx.byWallLine.get(m.wallId) || { upper: [] }).upper;
        uppers.forEach((oi) => {
          const o = ctx.members[oi];
          if (!isHoodM(o)) return;
          const ov = Math.min(m.right, o.right) - Math.max(m.left, o.left);
          if (ov <= RULE_EPS) return;
          const bottom = roleBaseY(o.def) + (((o.p.config || {}).position || {}).elevation || 0) / 1000;
          const above = bottom - wtTop;
          if (above < ctx.params.hoodAboveMin) out.push({
            appliesTo: [m.p.id, o.p.id], measuredValue: above, expectedValue: ctx.params.hoodAboveMin,
            message: `Extractor ${o.def.code || o.p.type} hangs only ${mm(above)} above the hob — ${mm(ctx.params.hoodAboveMin)} minimum for fire safety and headroom`,
          });
          else if (above > ctx.params.hoodAboveMax) out.push({
            appliesTo: [m.p.id, o.p.id], severity: "info", measuredValue: above, expectedValue: ctx.params.hoodAboveMax,
            message: `Extractor ${o.def.code || o.p.type} sits ${mm(above)} above the hob — beyond ${mm(ctx.params.hoodAboveMax)} the capture zone stops working well`,
          });
        });
      });
      return out;
    },
  },
  {
    id: "ergonomic.aisle-width", category: "ergonomic", defaultSeverity: "warning",
    title: "Aisle between facing runs",
    evaluate(ctx) {
      // facing PARALLEL walls whose base runs overlap laterally: the clear
      // aisle = wall-face distance − both runs' deepest cabinets.
      const out = [];
      const wallsArr = ctx.walls.filter((w) => ctx.graph.walls.get(w.id));
      const deepest = new Map();   // wallId → { d, span: [min,max] } of BASE members
      ctx.byWallLine.forEach((lines, wallId) => {
        let d = 0, lo = Infinity, hi = -Infinity;
        lines.base.forEach((mi) => {
          const m = ctx.members[mi];
          d = Math.max(d, m.def.depth || 0);
          lo = Math.min(lo, m.left); hi = Math.max(hi, m.right);
        });
        if (d > 0) deepest.set(wallId, { d, lo, hi });
      });
      for (let i = 0; i < wallsArr.length; i++) for (let j = i + 1; j < wallsArr.length; j++) {
        const A = wallsArr[i], B = wallsArr[j];
        const da = deepest.get(A.id), db = deepest.get(B.id);
        if (!da || !db) continue;
        const wa = ctx.graph.walls.get(A.id), wb = ctx.graph.walls.get(B.id);
        if (Math.abs(wa.dir[0] * wb.dir[0] + wa.dir[1] * wb.dir[1]) < 0.985) continue;  // not parallel
        // perpendicular distance between the wall midlines
        const n = [-wa.dir[1], wa.dir[0]];
        const dv = [B.start[0] - A.start[0], B.start[1] - A.start[1]];
        const gap = Math.abs(dv[0] * n[0] + dv[1] * n[1]) - WALL_THICKNESS - da.d - db.d;
        if (gap <= 0.02) continue;                     // same line / touching — not an aisle
        // lateral overlap of the two runs (project B's member span onto A's axis)
        const bOff = (B.start[0] - A.start[0]) * wa.dir[0] + (B.start[1] - A.start[1]) * wa.dir[1];
        const bDir = wa.dir[0] * wb.dir[0] + wa.dir[1] * wb.dir[1] >= 0 ? 1 : -1;
        const bSpan = [bOff + bDir * db.lo, bOff + bDir * db.hi].sort((x, y) => x - y);
        const overlap = Math.min(da.hi, bSpan[1]) - Math.max(da.lo, bSpan[0]);
        if (overlap <= 0.05) continue;                 // runs never face each other
        if (gap < ctx.params.aisleMin) out.push({
          appliesTo: [], measuredValue: gap, expectedValue: ctx.params.aisleMin,
          message: `Facing runs on ${A.id} and ${B.id} leave a ${mm(gap)} aisle — ${mm(ctx.params.aisleMin)} is needed to open doors and pass while someone is working`,
        });
      }
      return out;
    },
  },
  {
    id: "safety.fridge-beside-hob", category: "safety", defaultSeverity: "warning",
    title: "Fridge flanking the hob",
    evaluate(ctx) {
      const out = [];
      consecutivePairs(ctx, (a, b, gap) => {
        const pair = (isHobM(a) && isFridgeM(b)) || (isFridgeM(a) && isHobM(b));
        if (!pair || gap > ctx.params.fridgeHobGapMin) return;
        out.push({
          appliesTo: [a.p.id, b.p.id], measuredValue: Math.max(0, gap), expectedValue: ctx.params.fridgeHobGapMin,
          message: `Fridge stands ${mm(Math.max(0, gap))} from the hob — heat next to the compressor wall costs energy and shortens its life; keep ${mm(ctx.params.fridgeHobGapMin)} or a tall divider between them`,
        });
      });
      return out;
    },
  },
];

/* ── evaluation API ──────────────────────────────────────────────────────── */
const normalize = (rule, f) => ({
  ruleId: rule.id,
  category: rule.category,
  severity: f.severity || rule.defaultSeverity,
  appliesTo: f.appliesTo || [],
  passed: f.passed !== undefined ? f.passed : false,
  message: f.message,
  ...(f.measuredValue !== undefined ? { measuredValue: f.measuredValue } : {}),
  ...(f.expectedValue !== undefined ? { expectedValue: f.expectedValue } : {}),
  ...(f.suggestedFix ? { suggestedFix: f.suggestedFix } : {}),
});

/** evaluateRules({walls, placements, lib, params}, {include, exclude, categories})
 *  → { findings, ruleStatus, context }. Pure — input is never mutated. */
export function evaluateRules(input, opts = {}) {
  const ctx = buildRuleContext(input || {});
  const active = RULES.filter((r) =>
    (!opts.include || opts.include.includes(r.id)) &&
    (!opts.exclude || !opts.exclude.includes(r.id)) &&
    (!opts.categories || opts.categories.includes(r.category)));
  const findings = [];
  const ruleStatus = active.map((rule) => {
    const fs = (rule.evaluate(ctx) || []).map((f) => normalize(rule, f));
    findings.push(...fs);
    return { ruleId: rule.id, category: rule.category, passed: fs.every((f) => f.passed), findings: fs.length };
  });
  return { findings, ruleStatus, context: ctx };
}

/** Run ONE rule in isolation (rules are independent by construction). */
export function runRule(ruleId, input, opts = {}) {
  return evaluateRules(input, { ...opts, include: [ruleId] });
}

/* ── QUICK FIXES (2026-08-20 user: "in the problems add a fix button if the
 * user wants a quick fix") — the ONE place a suggestedFix becomes an actual
 * state mutation, so the panel button and any future auto-fix agree. Pure
 * apart from the explicit placement mutation; returns { ok, message } and
 * NEVER throws. The caller owns markDirty/bump. */
export function applyRuleFix(state, blob, fix) {
  if (!state || !fix) return { ok: false, message: "Nothing to fix" };
  const pl = (state.placements || []).find((p) => p && p.id === fix.placementId);
  if (fix.kind === "slide" || fix.kind === "close-gap") {
    if (!pl) return { ok: false, message: "Module not found" };
    const d = Number(fix.deltaM) || 0;
    if (!d) return { ok: false, message: "Nothing to move" };
    pl.absLeft = Math.max(0, (pl.absLeft || 0) + d);
    return { ok: true, message: "Moved " + Math.round(Math.abs(d) * 1000) + " mm " + (d < 0 ? "left" : "right") };
  }
  if (fix.kind === "hood") {
    if (!pl) return { ok: false, message: "Module not found" };
    const hood = ((((blob || {}).equipment || {}).hoods) || []).find((h) => h && h.code);
    if (!hood) return { ok: false, message: "No extractor in the Equipment Library yet" };
    pl.config = pl.config || {};
    pl.config.eqHoods = hood.code;
    return { ok: true, message: "Extractor " + ((hood.brand ? hood.brand + " " : "") + (hood.model || hood.code)) + " fitted" };
  }
  return { ok: false, message: "This one needs a manual decision" };
}

/** Which suggestedFix kinds the panel may offer as a one-click button. */
export function fixIsQuick(fix, blob) {
  if (!fix) return false;
  if (fix.kind === "slide" || fix.kind === "close-gap") return !!fix.placementId && !!(Number(fix.deltaM) || 0);
  if (fix.kind === "hood") return !!fix.placementId && ((((blob || {}).equipment || {}).hoods) || []).some((h) => h && h.code);
  return false;
}
